import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface PaginaProps {
  /** `completo`: tablas. `lectura`: configuracion, catalogo. */
  ancho?: "completo" | "lectura";
  /**
   * `natural`: la pagina crece y el scroll lo pone el `<main>` del shell.
   * `fijo`: la pagina mide exactamente el `<main>` y el scroll lo pone el
   * ultimo bloque (la lista), que es el unico de la pantalla.
   */
  alto?: "natural" | "fijo";
  className?: string;
  children: ReactNode;
}

const ANCHOS = {
  completo: "",
  lectura: "mx-auto max-w-3xl",
} as const;

/*
  El alto `fijo` existe para matar el doble scroll: el shell ya le da al
  `<main>` un alto exacto (`__root.tsx:227` y `:348`) y `h-full` lo copia, asi
  que la pagina no puede desbordar y lo unico que scrollea es el ultimo bloque.

  Las dos clases del ultimo hijo van aca y no en cada pantalla porque son la
  variante, no una decision de la ruta: quien escribe la pantalla pone los
  bloques en orden y el ultimo se lleva lo que sobra, sin escribir flex a mano.

  `min-h-0` ademas de `flex-1`: sin eso un hijo flex no baja de su alto de
  contenido y el `overflow-y-auto` de adentro no llega a activarse nunca. Es el
  mismo error que ya se cometio dos veces en el sidebar y en el `<main>`
  (`__root.tsx:110-111` y `:342-344`).

  Pero el piso no es 0 sino 16rem: con 0, en una notebook (1366x768) los
  bloques de arriba se comian todo el alto y el ultimo quedaba en 3px. En
  Ganancias los avisos de costo estimado dejaban la tabla «Por dia» y su
  Total en una raya, y como la pagina media justo el `<main>` la rueda no
  movia nada. Con el piso, si no entra la pagina desborda y scrollea el
  `<main>` (que tiene `overflow-y-auto`); si entra, todo sigue igual. Sigue
  siendo un alto definido, asi que el `overflow-y-auto` de adentro funciona.

  `gap-6` y no `space-y-6`: en una columna flex la separacion es del
  contenedor. Con `space-y` el margen lo carga el hijo que ademas tiene que
  estirarse, y el alto que reparte el flex deja de leerse de una sola clase.
  En `natural` se queda `space-y-6` tal cual: son 16 rutas que no se tocan.

  De ese ultimo hijo para abajo la cadena la sigue quien usa la variante: cada
  envoltorio intermedio lleva `h-full` (o es otra columna flex) hasta llegar a
  la `<Table alto="lleno">`, porque un `h-full` contra un padre de alto `auto`
  vuelve a ser `auto` y se pierde todo.
*/
const ALTOS = {
  natural: "space-y-6",
  fijo: "flex h-full flex-col gap-6 [&>*:last-child]:min-h-[16rem] [&>*:last-child]:flex-1",
} as const;

/**
 * Raiz de una ruta: un solo gap entre bloques (24px), tres anchos con nombre
 * —en vez de un max-w distinto por pantalla y sin centrar— y dos altos.
 */
export function Pagina({
  ancho = "completo",
  alto = "natural",
  className,
  children,
}: PaginaProps): JSX.Element {
  return <div className={cn(ALTOS[alto], ANCHOS[ancho], className)}>{children}</div>;
}
